<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  tourTitle: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, required: true },
  stepTitles: { type: Array, required: true },
  currentIndex: { type: Number, required: true },
  labels: { type: Object, required: true },
});

const emit = defineEmits([
  'close',
  'next',
  'previous',
  'goToStep',
  'dismissCurrent',
  'dismissAll',
]);

const isOptionsOpen = ref(false);
const stepsNavigation = ref(null);
const total = computed(() => props.stepTitles.length);
const isFirst = computed(() => props.currentIndex === 0);
const isLast = computed(() => props.currentIndex === total.value - 1);

watch(
  () => props.currentIndex,
  async currentIndex => {
    await nextTick();
    const currentStep = stepsNavigation.value?.querySelector(
      `[data-tour-step-index="${currentIndex}"]`
    );
    currentStep?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  },
  { immediate: true }
);
</script>

<template>
  <section
    class="flex max-h-[min(38rem,calc(100vh-2rem))] min-h-[26rem] w-full flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-background text-n-slate-12 shadow-2xl"
    role="dialog"
    aria-modal="true"
    :aria-label="tourTitle"
  >
    <header
      class="flex shrink-0 items-start justify-between gap-4 border-b border-n-weak bg-n-surface-1 px-6 py-5"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand ring-1 ring-n-brand/20"
        >
          <Icon icon="i-lucide-route" class="size-5" />
        </span>
        <div class="min-w-0">
          <p class="text-xs font-medium text-n-brand">
            {{ labels.eyebrow }}
          </p>
          <h2 class="truncate text-lg font-semibold text-n-slate-12">
            {{ tourTitle }}
          </h2>
        </div>
      </div>
      <div class="relative flex shrink-0 items-center gap-1">
        <Button
          icon="i-lucide-ellipsis"
          color="slate"
          variant="ghost"
          size="sm"
          :aria-label="labels.options"
          :aria-expanded="isOptionsOpen"
          @click="isOptionsOpen = !isOptionsOpen"
        />
        <Button
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          size="sm"
          :aria-label="labels.close"
          @click="emit('close')"
        />

        <div
          v-if="isOptionsOpen"
          class="absolute end-0 top-10 z-10 w-72 rounded-lg border border-n-weak bg-n-background p-2 shadow-xl"
        >
          <p class="px-2 pb-2 pt-1 text-xs font-medium text-n-slate-10">
            {{ labels.dismissLabel }}
          </p>
          <button
            type="button"
            class="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-start hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="emit('dismissCurrent')"
          >
            <Icon
              icon="i-lucide-panel-top-close"
              class="mt-0.5 size-4 shrink-0 text-n-brand"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ labels.dismissCurrent }}
              </span>
              <span class="mt-0.5 block text-xs leading-4 text-n-slate-10">
                {{ labels.dismissCurrentDescription }}
              </span>
            </span>
          </button>
          <button
            type="button"
            class="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-start hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="emit('dismissAll')"
          >
            <Icon
              icon="i-lucide-panels-top-left"
              class="mt-0.5 size-4 shrink-0 text-n-brand"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ labels.dismissAll }}
              </span>
              <span class="mt-0.5 block text-xs leading-4 text-n-slate-10">
                {{ labels.dismissAllDescription }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </header>

    <div class="flex min-h-0 flex-1">
      <nav
        ref="stepsNavigation"
        class="hidden w-56 shrink-0 overflow-y-auto border-e border-n-weak bg-n-surface-1 p-3 md:block"
        :aria-label="labels.steps"
      >
        <ol class="space-y-1">
          <li
            v-for="(stepTitle, index) in stepTitles"
            :key="`${index}-${stepTitle}`"
          >
            <button
              type="button"
              :data-tour-step-index="index"
              class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="
                index === currentIndex
                  ? 'bg-n-brand/10 font-medium text-n-brand'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              :disabled="index > currentIndex"
              @click="emit('goToStep', index)"
            >
              <span
                class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs"
                :class="
                  index <= currentIndex
                    ? 'bg-n-brand text-[var(--n-brand-contrast)]'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                <Icon
                  v-if="index < currentIndex"
                  icon="i-lucide-check"
                  class="size-3.5"
                />
                <span v-else>{{ index + 1 }}</span>
              </span>
              <span class="line-clamp-2 min-w-0">{{ stepTitle }}</span>
            </button>
          </li>
        </ol>
      </nav>

      <main class="min-w-0 flex-1 overflow-y-auto px-6 py-8 sm:px-8">
        <div class="mx-auto max-w-xl">
          <p
            class="mb-2 text-xs font-medium uppercase tracking-wide text-n-brand"
          >
            {{
              labels.progress
                .replace('{current}', currentIndex + 1)
                .replace('{total}', total)
            }}
          </p>
          <h3 class="text-xl font-semibold leading-7 text-n-slate-12">
            {{ title }}
          </h3>
          <p
            class="mt-4 whitespace-pre-line text-base leading-7 text-n-slate-11"
          >
            {{ description }}
          </p>
          <div
            class="mt-6 flex items-start gap-3 rounded-lg border border-n-brand/20 bg-n-brand/5 p-4"
          >
            <Icon
              icon="i-lucide-mouse-pointer-click"
              class="mt-0.5 size-4 shrink-0 text-n-brand"
            />
            <p class="text-sm leading-5 text-n-slate-11">
              {{ labels.highlightHint }}
            </p>
          </div>
        </div>
      </main>
    </div>

    <footer
      class="flex shrink-0 flex-col gap-4 border-t border-n-weak bg-n-surface-1 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="min-w-0 flex-1">
        <progress
          class="h-1.5 w-full overflow-hidden rounded-full accent-n-brand"
          :value="currentIndex + 1"
          :max="total"
        />
        <p class="mt-1.5 text-xs text-n-slate-10">
          {{
            labels.progress
              .replace('{current}', currentIndex + 1)
              .replace('{total}', total)
          }}
        </p>
      </div>
      <div class="flex items-center justify-end gap-2">
        <Button
          :label="labels.previous"
          icon="i-lucide-arrow-left"
          color="slate"
          variant="outline"
          :disabled="isFirst"
          @click="emit('previous')"
        />
        <Button
          :label="isLast ? labels.done : labels.next"
          :icon="isLast ? 'i-lucide-check' : 'i-lucide-arrow-right'"
          trailing-icon
          color="blue"
          @click="emit('next')"
        />
      </div>
    </footer>
  </section>
</template>
